import { createElement } from 'react';
import { EpicDevRequestPanel } from '@/features/project-board/components/EpicDevRequestPanel';
import type { DrawerChild, DrawerDoc, DrawerPayload } from '@/features/project-board/components/DetailDrawer';
import { EpicMembership, ItemKey } from '@/features/project-board/identity';
import { QaScheduleLabel } from '@/features/project-board/qa-schedule';
import { OwnerRoster } from '@/features/project-board/owners';
import type {
  BoardIndex,
  DevRequest,
  DevRequestSpecLink,
  Epic,
  Incident,
  Issue,
  QaTicket,
  Task,
} from '@/features/project-board/types';

/** 상세 패널에 무엇을 보여줄지 — 화면마다 같은 항목을 같은 방식으로 열게 한곳에 모은다. */

const EPIC_DOC_PRIORITY = [
  'PRD.md',
  'HLD.md',
  'TASKS.md',
  'PROGRESS.md',
  'REVIEW.md',
  'VALIDATION.md',
  'spec-review.md',
];

const fileName = (path: string): string => path.split('/').pop() ?? path;

export class DrawerPayloads {
  static task(task: Task, epic: Epic | undefined, siblings: Task[] = []): DrawerPayload {
    const docs: DrawerDoc[] = [];
    if (task.documentPath) docs.push({ label: 'task 문서', path: task.documentPath });
    if (epic) {
      for (const path of epic.documentPaths.filter((candidate) =>
        /\/(TASKS|PROGRESS|PRD|HLD|REVIEW|VALIDATION)\.md$/.test(candidate),
      )) {
        docs.push({ label: fileName(path), path });
      }
    }

    return {
      key: task.uid,
      kind: 'task',
      source: task.source,
      title: task.title || '(제목 없음)',
      // task에서 에픽으로 올라갈 수 있어야 지라의 에픽-스토리 왕복이 된다.
      parent: epic ? { key: ItemKey.epic(epic), label: epic.title, open: () => DrawerPayloads.epic(epic, siblings) } : undefined,
      meta: [
        { label: '상태', value: `${task.statusToken || task.status}${task.statusNote ? ` — ${task.statusNote}` : ''}` },
        { label: '에픽', value: epic ? epic.title : task.epicSlug },
        { label: '담당', value: OwnerRoster.assigneeOf(task, epic) || '-' },
        { label: 'Repo', value: task.repo || '-' },
        { label: '그룹', value: task.group || '-' },
        { label: '예상 공수', value: task.estimate || '-' },
        {
          label: '선행',
          value: task.dependsOn.length
            ? `${task.dependsOn.join(', ')}${task.blockedBy.length ? ` (미완 ${task.blockedBy.join(', ')})` : ''}`
            : '없음',
        },
        { label: '브랜치', value: task.branch || '' },
        { label: '갱신', value: task.updatedAt || '-' },
        { label: '진행 메모', value: task.notes || '' },
      ],
      docs,
    };
  }

  static epic(epic: Epic, tasks: Task[] = []): DrawerPayload {
    const depth = epic.path.split('/').length;
    const rootDocs = epic.documentPaths
      .filter((path) => path.split('/').length === depth + 1)
      .map((path) => ({ label: fileName(path), path }))
      .sort((left, right) => {
        const rank = (label: string) => {
          const at = EPIC_DOC_PRIORITY.indexOf(label);
          return at === -1 ? 99 : at;
        };
        return rank(left.label) - rank(right.label) || left.label.localeCompare(right.label);
      });
    const lldDocs = epic.documentPaths
      .filter((path) => path.includes('/lld/'))
      .map((path) => ({ label: `lld/${fileName(path)}`, path }));

    return {
      key: ItemKey.epic(epic),
      kind: 'epic',
      source: epic.source,
      title: epic.title,
      meta: [
        { label: '기간', value: `${epic.year}-${epic.month}` },
        { label: '개발 담당자', value: epic.owner || '-' },
        {
          label: '현재 단계',
          value: `${epic.stageLabel}${epic.skippedStages.length ? ` (건너뜀: ${epic.skippedStages.join(', ')} · 연속 통과 ${epic.gateStageLabel})` : ''}`,
        },
        // 다음 관문이 없으면(마지막 단계까지 채웠으면) null이라 이 줄 자체가 빠진다 — 상세 패널은 빈 값을 거른다.
        { label: '다음 단계', value: epic.nextStageLabel },
        { label: '진행률', value: `${epic.donePct}% (완료 ${epic.counts.done} / 활성 ${epic.activeCount})` },
        {
          label: 'task',
          value: `대기 ${epic.counts.todo} · 진행 ${epic.counts.progress} · 완료 ${epic.counts.done} · 제외 ${epic.counts.dropped}`,
        },
        { label: '출처', value: createElement(EpicDevRequestPanel, { epic }) },
        { label: '경로', value: epic.path },
        { label: '요약', value: epic.summary || '' },
      ],
      children: { label: '연결된 task', items: DrawerPayloads.taskChildren(epic, tasks) },
      docs: [...rootDocs, ...lldDocs],
    };
  }

  static qa(ticket: QaTicket, epic: Epic, documentPath: string): DrawerPayload {
    const qaDocs = epic.documentPaths
      // QA 티켓 상세에는 티켓의 근거 문서만 노출한다. 결정표·전이표·리스크 분석 등은
      // 별도 산출물이라 체크리스트 티켓을 열었을 때 섞이면 안 된다.
      .filter((path) => path.includes('/qa/') && /(?:체크리스트|checklist)/i.test(path))
      .map((path) => ({ label: `qa/${path.split('/qa/')[1]}`, path }))
      .sort((left, right) => left.label.localeCompare(right.label));
    const status = ticket.status;
    const reviewPeriod = QaScheduleLabel.period(epic.qaSchedule);
    const field = (label: string, value: string) => value ? `\n### ${label}\n${value}\n` : '';
    const target = [
      `- 확인 항목: **${ticket.title}**`,
      `- 확인 화면: ${ticket.details.screen || '체크리스트에 화면 정보 없음'}`,
      `- 말단 ID: ${ticket.details.terminal || '없음'}`,
    ].join('\n');
    const execution = ticket.executions.length > 0
      ? ticket.executions.map((testCase) => [
          `### ${testCase.tcId}`,
          `**사전 조건**\n\n${testCase.precondition}`,
          `**체크 절차**\n\n${testCase.steps}`,
          `**정상 판정**\n\n${testCase.expected}`,
          testCase.memo ? `**메모**\n\n${testCase.memo}` : '',
        ].filter(Boolean).join('\n\n')).join('\n\n---\n\n')
      : `1. ${ticket.details.screen || '대상 화면'}으로 이동한다.\n2. **${ticket.title}** 조건을 직접 확인한다.\n3. 조건 충족 여부와 확인 근거를 기록한다.`;
    return {
      key: `${ItemKey.epic(epic)}::qa::${ticket.id}`,
      kind: 'qa',
      source: epic.source,
      title: ticket.title,
      meta: [
        { label: '상태', value: status },
        { label: '종류', value: ticket.kind },
        { label: '우선순위', value: ticket.priority },
        ...(ticket.details.tcIds?.length ? [{ label: 'TC-ID', value: ticket.details.tcIds.join(', ') }] : []),
        { label: '에픽', value: epic.title },
        ...(reviewPeriod ? [{ label: 'QA 검증 기간', value: reviewPeriod }] : []),
        ...(epic.qaSchedule?.releaseTarget ? [{ label: '배포 목표일', value: epic.qaSchedule.releaseTarget }] : []),
        ...(epic.qaSchedule?.note ? [{ label: '일정 제약', value: epic.qaSchedule.note }] : []),
        { label: 'QA 엔지니어', value: OwnerRoster.QA_ENGINEER },
        { label: 'QA 문서', value: documentPath },
      ],
      docs: qaDocs,
      body: {
        label: `QA 티켓 · ${ticket.id}`,
        text: `# ${ticket.title}\n\n- 상태: **${status}**\n- 종류: **${ticket.kind}**\n- 우선순위: **${ticket.priority}**\n- 연결 에픽: **${epic.title}**\n\n## 무엇을 확인하나\n${target}\n\n## 어떻게 체크하나\n${execution}\n${field('추가 판정 기준', ticket.details.criteria)}${field('왜 확인하는가', ticket.details.why)}${field('출처', ticket.details.source)}${field('역기획서 발췌', ticket.details.excerpt)}`,
      },
    };
  }

  /** 에픽에 속한 task — **번호 오름차순**. 상태순으로 섞으면 문서의 task 번호 순서와 어긋나 찾기 어렵다. */
  static taskChildren(epic: Epic, tasks: Task[]): DrawerChild[] {
    const siblings = EpicMembership.tasksOf(tasks, epic);
    return [...siblings]
      .sort((left, right) => left.taskId.localeCompare(right.taskId, undefined, { numeric: true }))
      .map((task) => ({
        key: task.taskId,
        label: task.titleHead || task.title || '(제목 없음)',
        status: task.status,
        statusToken: task.statusToken,
        hint: [OwnerRoster.assigneeOf(task, epic), task.repo, task.estimate].filter(Boolean).join(' · '),
        dependsOn: task.dependsOn,
        blockedBy: task.blockedBy,
        open: () => DrawerPayloads.task(task, epic, tasks),
      }));
  }

  static issue(issue: Issue): DrawerPayload {
    const docs = issue.documentPaths
      .map((path) => ({ label: fileName(path), path }))
      .sort((left, right) => (left.label === 'issue.md' ? -1 : right.label === 'issue.md' ? 1 : 0));

    return {
      key: ItemKey.issue(issue),
      kind: 'issue',
      source: issue.source,
      title: issue.title,
      meta: [
        {
          label: '상태',
          value: `${issue.stageLabel}${issue.skippedStages.length ? ` (건너뜀: ${issue.skippedStages.join(', ')})` : ''}`,
        },
        { label: '제보일', value: issue.reportedAt || '-' },
        {
          label: '처리자',
          value: issue.handledBy
            ? `${OwnerRoster.memberOf(issue.handledBy) || issue.handledBy}${issue.handledAt ? ` · ${issue.handledAt.slice(0, 10)}` : ''}`
            : '-',
        },
        { label: 'Repo', value: issue.repos.length ? issue.repos.join(', ') : '-' },
        { label: '단계', value: issue.stages.map((stage) => `${DrawerPayloads.stageMark(stage.fill)} ${stage.label}`).join('  ') },
        { label: '갱신', value: issue.updatedAt ?? '-' },
      ],
      docs,
    };
  }

  static incident(incident: Incident): DrawerPayload {
    const docs = incident.documentPaths
      .map((path) => ({ label: fileName(path), path }))
      .sort((left, right) => (left.label === 'incident.md' ? -1 : right.label === 'incident.md' ? 1 : 0));

    return {
      key: ItemKey.incident(incident),
      kind: 'incident',
      source: incident.source,
      title: incident.title,
      meta: [
        {
          label: '상태',
          value: `${incident.stageLabel}${incident.skippedStages.length ? ` (건너뜀: ${incident.skippedStages.join(', ')})` : ''}`,
        },
        { label: '발생일', value: incident.reportedAt || '-' },
        {
          label: '처리자',
          value: incident.handledBy
            ? `${OwnerRoster.memberOf(incident.handledBy) || incident.handledBy}${incident.handledAt ? ` · ${incident.handledAt.slice(0, 10)}` : ''}`
            : '-',
        },
        { label: 'Repo', value: incident.repos.length ? incident.repos.join(', ') : '-' },
        {
          label: '단계',
          value: incident.stages.map((stage) => `${DrawerPayloads.stageMark(stage.fill)} ${stage.label}`).join('  '),
        },
        { label: '갱신', value: incident.updatedAt ?? '-' },
      ],
      docs,
    };
  }

  static stageMark(fill: string): string {
    if (fill === 'filled') return '✓';
    return fill === 'partial' ? '▲' : '·';
  }

  /**
   * 개발요청 하나.
   *
   * 다른 항목과 달리 **본문을 받아 올 데가 없다** — 인입 정본이 이미 목록 응답에 실려 온다. 그래서 `docs`가
   * 비고 `body`로 간다. `source`는 아예 넣지 않는다: 그 필드는 문서를 받아 올 하네스 저장소를 가리키는데
   * 개발요청은 하네스에서 온 것이 아니라 그런 저장소가 없다.
   *
   * 첨부·개정 이력·스펙 링크는 화면이 그릴 것이라 `extras`로 받는다 — 여기서 조립하면 이 파일이 JSX를 알아야 한다.
   */
  static devRequest(
    request: DevRequest,
    specs: DevRequestSpecLink[],
    extras: { header?: React.ReactNode; footer?: React.ReactNode } = {},
  ): DrawerPayload {
    return {
      key: `#${request.id}`,
      kind: 'dev-request',
      title: request.title,
      meta: [
        { label: '요청자', value: request.requester },
        { label: '수정자', value: request.reviser ?? '' },
        // 콘솔 역등록은 채널이 없다 — 빈 값으로 행을 그리면 "있는데 이름이 빈 것"으로 읽힌다.
        ...(request.channel === null ? [] : [{ label: '채널', value: request.channel }]),
        { label: '개정', value: request.revision > 1 ? `rev ${request.revision}` : 'rev 1 (개정 없음)' },
        { label: '요청', value: DrawerPayloads.localTime(request.requestedAt) },
        { label: '접수', value: DrawerPayloads.localTime(request.receivedAt) },
        { label: '갱신', value: request.revision > 1 ? DrawerPayloads.localTime(request.updatedAt) : '' },
        { label: 'slug', value: request.slug },
        // 연결 스펙은 본문 머리에 **링크로** 실린다(`LinkedSpecs`) — 여기 평문으로 또 적으면 같은 것이 두 번 보인다.
        { label: '상태', value: specs.length ? `스펙 ${specs.length}건 연결됨` : '미착수' },
        { label: '첨부', value: request.attachments.length ? `${request.attachments.length}건` : '' },
      ],
      docs: [],
      body: {
        label: `${request.source} · ${request.nativeId}`,
        text: request.body,
        header: extras.header,
        footer: extras.footer,
      },
    };
  }

  /** 저장은 UTC ISO다 — 읽는 사람은 한국 시간으로 본다. */
  static localTime(iso: string): string {
    return new Date(iso).toLocaleString('ko-KR', { dateStyle: 'short', timeStyle: 'short' });
  }

  /**
   * 공유 링크(`item`/`kind` 쿼리)로 돌아왔을 때 그 항목의 payload를 다시 만든다 — 화면에서 클릭할 때와
   * 같은 조립 함수를 그대로 쓴다. 개발요청은 색인이 아니라 별도 인입에서 오고 그 화면은 `ViewFrame`도
   * 거치지 않아 여기서 다루지 않는다.
   */
  static resolveLinked(
    index: Pick<BoardIndex, 'epics' | 'tasks' | 'issues' | 'incidents'>,
    kind: string,
    key: string,
  ): DrawerPayload | undefined {
    switch (kind) {
      case 'epic': {
        const epic = index.epics.find((candidate) => ItemKey.epic(candidate) === key);
        return epic ? DrawerPayloads.epic(epic, index.tasks) : undefined;
      }
      case 'task': {
        const task = index.tasks.find((candidate) => candidate.uid === key);
        return task ? DrawerPayloads.task(task, EpicMembership.epicOf(index.epics, task), index.tasks) : undefined;
      }
      case 'issue': {
        const issue = index.issues.find((candidate) => ItemKey.issue(candidate) === key);
        return issue ? DrawerPayloads.issue(issue) : undefined;
      }
      case 'incident': {
        const incident = index.incidents.find((candidate) => ItemKey.incident(candidate) === key);
        return incident ? DrawerPayloads.incident(incident) : undefined;
      }
      default:
        return undefined;
    }
  }
}
